import React, { useEffect, useMemo, useState } from 'react';
import { api } from '../../api/client';
import type { Workday } from '../../api/types';
import { Modal } from '../../components/Modal';
import { MapPreview } from '../../components/MapPreview';
import { Calendar, type CalendarEvent, type CalendarView } from '../../components/calendar/Calendar';
import { useToast } from '../../components/Toast';
import { useInterval } from '../../hooks/useInterval';
import { fmtDateISO, fmtHM, fmtTime, ymdLocal } from '../../utils/time';
import { buildSegments, computeDurationsClient, computeStatus } from '../../utils/workday';

function isoYYYYMMDD(d: Date) {
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

function startOfWeekMonday(d: Date) {
  const x = new Date(d);
  const day = (x.getDay() + 6) % 7; // 0=Mon
  x.setDate(x.getDate() - day);
  x.setHours(0, 0, 0, 0);
  return x;
}

function endOfWeekMonday(d: Date) {
  const s = startOfWeekMonday(d);
  const e = new Date(s);
  e.setDate(e.getDate() + 6);
  return e;
}

function startOfMonth(d: Date) {
  return new Date(d.getFullYear(), d.getMonth(), 1);
}

function endOfMonth(d: Date) {
  return new Date(d.getFullYear(), d.getMonth() + 1, 0);
}

function buildMonthGrid(base: Date) {
  const first = startOfMonth(base);
  const last = endOfMonth(base);
  const start = new Date(first);
  // monday as first day
  const day = (start.getDay() + 6) % 7; // 0=Mon
  start.setDate(start.getDate() - day);

  const cells: Date[] = [];
  for (let i = 0; i < 42; i++) {
    const x = new Date(start);
    x.setDate(start.getDate() + i);
    cells.push(x);
  }
  return { first, last, cells };
}

function rangeForView(view: CalendarView, base: Date) {
  if (view === 'week') {
    return { from: isoYYYYMMDD(startOfWeekMonday(base)), to: isoYYYYMMDD(endOfWeekMonday(base)) };
  }
  // month (griglia completa)
  const grid = buildMonthGrid(base);
  return { from: isoYYYYMMDD(grid.cells[0]), to: isoYYYYMMDD(grid.cells[41]) };
}

export function DashboardPage() {
  const { push } = useToast();
  const [view, setView] = useState<CalendarView>('month');
  const [base, setBase] = useState(() => new Date());
  const [items, setItems] = useState<Workday[]>([]);
  const [totals, setTotals] = useState<{ workMin: number; breakMin: number; netMin: number } | null>(null);

  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState<Workday | null>(null);

  const [now, setNow] = useState(() => new Date());
  useInterval(() => setNow(new Date()), 1000);

  const range = useMemo(() => rangeForView(view, base), [view, base.getTime()]);

  async function load() {
    try {
      const r = await api.get<{ items: Workday[] }>(`/api/me/workdays?from=${range.from}&to=${range.to}`);
      setItems(r.items);

      // riepilogo solo in vista mese
      if (view === 'month') {
        const summary = await api.get<{ totals: any }>(`/api/me/summary?scope=month&date=${isoYYYYMMDD(base)}`);
        setTotals(summary.totals);
      } else {
        setTotals(null);
      }
    } catch (e: any) {
      push({ title: 'Errore caricamento', body: e?.data?.error || e?.message });
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view, range.from, range.to, base.getMonth(), base.getFullYear()]);

  const events: CalendarEvent<Workday>[] = useMemo(() => {
    const ev: CalendarEvent<Workday>[] = [];
    for (const w of items) {
      const segments = buildSegments(w, now);
      for (const s of segments) {
        ev.push({
          id: s.id,
          title: s.label,
          subtitle: `${fmtTime(s.start.toISOString())}–${fmtTime(s.end.toISOString())}`,
          kind: s.kind,
          start: s.start,
          end: s.end,
          data: w,
        });
      }
    }
    return ev;
  }, [items, now]);

  const byDay = useMemo(() => {
    const map = new Map<string, Workday>();
    for (const w of items) {
      const key = ymdLocal(w.work_date);
      map.set(key, w);
    }
    return map;
  }, [items]);

  function openWorkday(w: Workday | null) {
    setSelected(w);
    setOpen(true);
  }

  return (
    <div className="grid" style={{ gap: 16 }}>
      <div className="card calCard">
        <Calendar
          view={view}
          baseDate={base}
          onViewChange={setView}
          onBaseDateChange={setBase}
          events={events}
          onSelectEvent={(e) => openWorkday(e.data || null)}
          onSelectDate={(d) => {
            // tap su un giorno nel mese: passa alla settimana di quel giorno e (se presente) apri i dettagli
            const key = isoYYYYMMDD(d);
            const w = byDay.get(key) || null;
            setBase(d);
            setView('week');
            if (w) openWorkday(w);
          }}
        />
      </div>


      <Modal
        open={open}
        onClose={() => setOpen(false)}
        title={selected ? `Dettagli ${fmtDateISO(selected.work_date)}` : 'Nessuna timbratura'}
      >
        {selected ? (
          <div style={{ display: 'grid', gap: 12 }}>
            <div className="card" style={{ background: 'var(--gray-200)', boxShadow: 'none' }}>
              <div className="small" style={{ marginBottom: 10 }}>
                Stato: <b>{computeStatus(selected)}</b>
              </div>

              <div style={{ display: 'grid', gap: 10 }}>
                <div className="row" style={{ justifyContent: 'space-between' }}>
                  <span>Inizio lavoro</span>
                  <b>{fmtTime(selected.start_work_at)}</b>
                </div>
                {selected.start_work_loc ? (
                  <div className="small">📍 {selected.start_work_loc.address || `${selected.start_work_loc.lat}, ${selected.start_work_loc.lon}`}</div>
                ) : null}

                <div className="row" style={{ justifyContent: 'space-between' }}>
                  <span>Inizio pausa</span>
                  <b>{fmtTime(selected.start_break_at)}</b>
                </div>
                {selected.start_break_loc ? (
                  <div className="small">📍 {selected.start_break_loc.address || `${selected.start_break_loc.lat}, ${selected.start_break_loc.lon}`}</div>
                ) : null}

                <div className="row" style={{ justifyContent: 'space-between' }}>
                  <span>Fine pausa</span>
                  <b>{fmtTime(selected.end_break_at)}</b>
                </div>
                {selected.end_break_loc ? (
                  <div className="small">📍 {selected.end_break_loc.address || `${selected.end_break_loc.lat}, ${selected.end_break_loc.lon}`}</div>
                ) : null}

                <div className="row" style={{ justifyContent: 'space-between' }}>
                  <span>Fine lavoro</span>
                  <b>{fmtTime(selected.end_work_at)}</b>
                </div>
                {selected.end_work_loc ? (
                  <div className="small">📍 {selected.end_work_loc.address || `${selected.end_work_loc.lat}, ${selected.end_work_loc.lon}`}</div>
                ) : null}
              </div>

              <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid var(--border)' }}>
                {(() => {
                  const d = computeDurationsClient(selected, now);
                  return (
                    <div className="row" style={{ justifyContent: 'space-between' }}>
                      <span>Netto</span>
                      <b>{fmtHM(d.netMin)}</b>
                    </div>
                  );
                })()}
              </div>
            </div>

            {/* Mappe in ordine evento (più leggibile) */}
            {selected.start_work_loc ? <MapPreview loc={selected.start_work_loc} /> : null}
            {selected.start_break_loc ? <MapPreview loc={selected.start_break_loc} /> : null}
            {selected.end_break_loc ? <MapPreview loc={selected.end_break_loc} /> : null}
            {selected.end_work_loc ? <MapPreview loc={selected.end_work_loc} /> : null}
          </div>
        ) : (
          <div className="small">Nessun dato per questo giorno.</div>
        )}
      </Modal>
    </div>
  );
}
